import { useEffect, useRef, useState, useCallback } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useBranding } from "../../hooks/useBranding";
import styles from "./AdminLayout.module.css";

// ─── Nav config ──────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { label: "Dashboard", to: "/administrator" },
  {
    label: "View Site",
    to: "/home",
    external: true,
  },
  {
    label: "Users",
    children: [
      { label: "List Users", to: "/administrator/users" },
      { label: "Add User", to: "/administrator/users/add" },
      { label: "Unvalidated Users", to: "/administrator/users/unvalidated" },
      { label: "User Fields", to: "/administrator/profilefields" },
    ],
  },
  { label: "Groups", to: "/administrator/groups" },
  { label: "Events", to: "/administrator/events" },
  { label: "Content", to: "/administrator/content" },
  { label: "Notifications", to: "/administrator/notifications" },
  {
    label: "Settings",
    children: [
      { label: "Basic Settings", to: "/administrator/settings" },
      { label: "Menu Builder", to: "/administrator/menu-builder" },
      { label: "Custom Pages", to: "/administrator/pages" },
    ],
  },
];

// ─── Dropdown component ──────────────────────────────────────────────────────

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const skipClose = useRef(false);
  const location = useLocation();

  const isChildActive = item.children?.some((c) =>
    location.pathname === c.to || location.pathname.startsWith(c.to + "/")
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skipClose.current) {
        skipClose.current = false;
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    skipClose.current = true;
    setOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.dropdownWrapper} ref={wrapperRef}>
      <button
        className={`${styles.navItem} ${isChildActive ? styles.navItemActive : ""}`}
        onMouseDown={handleToggle}
        type="button"
      >
        {item.label}
        <span className={styles.navItemCaret}>&#9662;</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              className={styles.dropdownItem}
              onClick={() => setOpen(false)}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AdminLayout ─────────────────────────────────────────────────────────────

export default function AdminLayout() {
  const { user } = useSelector((state) => ({ user: state.user }));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logoUrl } = useBranding();

  const userInfo = user?.userinfo;
  const isAdmin = userInfo?.accountRole === "admin";

  // Redirect non-admin users
  useEffect(() => {
    if (userInfo && !isAdmin) {
      navigate("/home", { replace: true });
    }
    if (!userInfo) {
      navigate("/login", { replace: true });
    }
  }, [userInfo, isAdmin, navigate]);

  // Close mobile menu on route change
  const location = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // If not admin, show access denied (brief flash before redirect)
  if (!userInfo || !isAdmin) {
    return (
      <div className={styles.adminShell}>
        <div className={styles.accessDenied}>
          <div className={styles.accessDeniedTitle}>Access Denied</div>
          <div className={styles.accessDeniedText}>
            You must be an administrator to view this page.
          </div>
          <Link to="/home" className={styles.backLink}>
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminShell}>
      {/* ── Header / Nav Bar ── */}
      <header className={styles.header}>
        {/* Logo */}
        <Link to="/administrator" className={styles.logoArea}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="NIHSS VDS Admin"
              className={styles.logoImg}
            />
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className={styles.nav}>
          {NAV_ITEMS.map((item) => {
            if (item.children) {
              return <NavDropdown key={item.label} item={item} />;
            }

            if (item.external) {
              return (
                <Link key={item.to} to={item.to} className={styles.navItem}>
                  {item.label}
                </Link>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/administrator"}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Mobile hamburger */}
        <button
          className={styles.hamburger}
          onClick={() => setMobileOpen((prev) => !prev)}
          type="button"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? "\u2715" : "\u2630"}
        </button>
      </header>

      {/* ── Mobile Nav Overlay ── */}
      <div
        className={`${styles.mobileOverlay} ${mobileOpen ? styles.open : ""}`}
        onClick={() => setMobileOpen(false)}
      />
      <div className={`${styles.mobileMenu} ${mobileOpen ? styles.open : ""}`}>
        {NAV_ITEMS.map((item) => {
          if (item.children) {
            return (
              <div key={item.label}>
                <div className={styles.mobileGroupLabel}>{item.label}</div>
                {item.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    className={({ isActive }) =>
                      `${styles.mobileNavItem} ${styles.mobileSubItem} ${
                        isActive ? styles.mobileNavItemActive : ""
                      }`
                    }
                    onClick={() => setMobileOpen(false)}
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/administrator"}
              className={({ isActive }) =>
                `${styles.mobileNavItem} ${
                  isActive ? styles.mobileNavItemActive : ""
                }`
              }
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </NavLink>
          );
        })}
      </div>

      {/* ── Content ── */}
      <main className={styles.content}>
        <Outlet />
      </main>
    </div>
  );
}
